import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Trash2, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { createClient } from '@supabase/supabase-js';

type Props = {
  onBack: () => void;
  onManageCategory?: (category: string) => void;
};

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseAnonKey);

const CATEGORIES = [
  { id: 'dietetiques', name: 'Recettes Diététiques' },
  { id: 'entrees', name: 'Entrées' },
  { id: 'plats-principaux', name: 'Plats Principaux' },
  { id: 'desserts', name: 'Desserts' },
  { id: 'boissons', name: 'Boissons' },
  { id: 'sauces', name: 'Sauces' },
];

export default function AdminPanel({ onBack, onManageCategory }: Props) {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<Array<{ firebase_uid: string; email: string }>>([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaveAdmin, setIsSaveAdmin] = useState(false);
  const [isMainAdmin, setIsMainAdmin] = useState(false);

  useEffect(() => {
    const loadAdmins = async () => {
      if (!user) return;

      try {
        const { data: adminData } = await supabase
          .from('admin_users')
          .select('firebase_uid');

        setIsMainAdmin(!!adminData?.some(a => a.firebase_uid === user.id));

        const { data: allAdmins } = await supabase
          .from('admin_users')
          .select('firebase_uid, email');

        if (allAdmins) {
          setAdmins(allAdmins);
        }
      } catch (error) {
        console.error('Error loading admins:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAdmins();
  }, [user]);

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAdminEmail || !isMainAdmin) return;

    setIsSaveAdmin(true);
    try {
      await supabase
        .from('admin_users')
        .insert([{
          firebase_uid: 'temp-' + Date.now(),
          email: newAdminEmail
        }]);

      setAdmins([...admins, { firebase_uid: 'temp-' + Date.now(), email: newAdminEmail }]);
      setNewAdminEmail('');
    } catch (error) {
      console.error('Error adding admin:', error);
    } finally {
      setIsSaveAdmin(false);
    }
  };

  const handleRemoveAdmin = async (firebaseUid: string) => {
    if (!isMainAdmin) return;

    try {
      await supabase
        .from('admin_users')
        .delete()
        .eq('firebase_uid', firebaseUid);

      setAdmins(admins.filter(a => a.firebase_uid !== firebaseUid));
    } catch (error) {
      console.error('Error removing admin:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (!isMainAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 pb-16">
        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Retour
            </button>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-gray-600 font-semibold mb-2">Accès refusé</p>
            <p className="text-gray-500">Seuls les administrateurs principaux peuvent accéder à cette page</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="mb-4">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Retour
            </button>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Panneau d'Administration</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-4">Gérer les Catégories de Recettes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {CATEGORIES.map(category => (
              <button
                key={category.id}
                onClick={() => onManageCategory?.(category.id)}
                className="text-left p-4 bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg hover:shadow-md transition-shadow"
              >
                <h3 className="font-semibold text-orange-900">{category.name}</h3>
                <p className="text-sm text-orange-700 mt-1">Gérer les recettes</p>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-4">Gérer les Administrateurs</h2>

          <form onSubmit={handleAddAdmin} className="mb-6">
            <div className="flex gap-3">
              <input
                type="email"
                placeholder="Email du nouvel administrateur"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <button
                type="submit"
                disabled={isSaveAdmin}
                className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 flex items-center gap-2 disabled:opacity-50"
              >
                <Plus className="h-5 w-5" />
                Ajouter
              </button>
            </div>
          </form>

          <div className="space-y-2">
            {admins.map(admin => (
              <div
                key={admin.firebase_uid}
                className="flex items-center justify-between bg-gray-50 p-4 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-gray-500" />
                  <span className="font-medium">{admin.email}</span>
                </div>
                <button
                  onClick={() => handleRemoveAdmin(admin.firebase_uid)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
